import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { cn } from '@/lib/utils';

import { formatCount } from './formatCount';
import { FOCUS_RING } from './pillClasses';

export interface FilterChipOption<T extends string> {
  value: T;
  label: string;
  /** Count over the unsearched list. Always shown, including 0. */
  count: number;
}

export interface FilterChipGroupProps<T extends string> {
  /** The group's accessible name, e.g. "Show". Not drawn. */
  label: string;
  options: readonly FilterChipOption<T>[];
  value: T;
  onChange: (next: T) => void;
  className?: string;
}

/**
 * The chip, as People drew it (spec 226 research R1): a rounded outline with the
 * label and its count, tinted and outlined in the primary when picked. The
 * 44 px floor on a coarse pointer comes from `touch-target` and the padding —
 * never a `min-h`, which would override the floor (memory: touch-target loses
 * to min-h). People's 36 px comes from `h-9`: a plain height loses to the
 * floor's min-height on coarse pointers, so both hold.
 */
const CHIP = `inline-flex h-9 items-center max-sm:shrink-0 gap-1.5 rounded-full border px-3 py-1 text-sm whitespace-nowrap touch-target ${FOCUS_RING}`;
const CHIP_CHECKED = 'border-primary/50 bg-primary/10 text-foreground';
const CHIP_IDLE = 'border-border bg-card text-foreground hover:bg-accent/50';

/**
 * A row of filter chips picking one view of a list (spec 226 FR-001..004):
 * one radio group, exactly one chip selected, a roving tab stop on the selected
 * chip, arrows to move and select, Home and End to the ends.
 *
 * On a phone the row scrolls sideways inside its card rather than squeezing,
 * with the shared `overflow-fade` at its trailing edge while there is more to
 * the right (FR-011, research R6). Wide screens wrap instead. The page never
 * scrolls sideways: the group is `max-w-full min-w-0`.
 */
export function FilterChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: FilterChipGroupProps<T>) {
  const group = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const element = group.current;
    if (!element) return;
    const measure = () => {
      const more =
        element.scrollWidth - element.clientWidth - element.scrollLeft > 1;
      setOverflowing(more);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    element.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      element.removeEventListener('scroll', measure);
    };
  }, [options]);

  const select = (index: number) => {
    const option = options.at(index);
    if (!option) return;
    onChange(option.value);
    const chips =
      group.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    chips?.[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = options.findIndex((option) => option.value === value);
    const last = options.length - 1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        select(current >= last ? 0 : current + 1);
        return;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        select(current <= 0 ? last : current - 1);
        return;
      case 'Home':
        event.preventDefault();
        select(0);
        return;
      case 'End':
        event.preventDefault();
        select(last);
        return;
      default:
    }
  };

  return (
    <div
      ref={group}
      role="radiogroup"
      aria-label={label}
      data-overflowing={overflowing ? 'true' : undefined}
      onKeyDown={onKeyDown}
      className={cn(
        'overflow-fade scrollbar-none flex max-w-full min-w-0 flex-wrap gap-1.5 max-sm:flex-nowrap max-sm:overflow-x-auto',
        className,
      )}
    >
      {options.map((option) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => {
              if (!checked) onChange(option.value);
            }}
            className={cn(CHIP, checked ? CHIP_CHECKED : CHIP_IDLE)}
          >
            {option.label}{' '}
            <span
              className={cn(
                'tabular-nums',
                checked ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {formatCount(option.count)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
